import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '../config/env';


const STORAGE_KEY = 'CurrentHealthMessage';
const POLLING_INTERVAL = 10000;

type MessageType = 'error' | 'info' | 'warning';
interface HealthMessage {
    id: string;
    content: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    isDismissed?: boolean;
    type: MessageType;
}

interface HealthStatusResponse {
    success: boolean;
    message: HealthMessage;
}


export const useHealthStatusMessagePoller = () => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();


    const [storedData, setStoredData] = useState<HealthStatusResponse | null>(null);
    const storedDataRef = useRef<HealthStatusResponse | null>(null);
    const HEALTH_STAT_API = env.healthStatusApi

    useEffect(() => {
        storedDataRef.current = storedData;
    }, [storedData]);

    const displayStatus = useCallback(
        async (message: HealthMessage) => {

            enqueueSnackbar(
                message.content,
                {
                    variant: message.type,
                    persist: true,
                    key: 'health-status-message',
                    onClose: async (event, reason, myKey) => {
                        if (reason === 'instructed') {
                            if (myKey === 'health-status-message') {
                                if (storedDataRef.current) {
                                    setStoredData({
                                        ...storedDataRef.current,
                                        message: {
                                            ...storedDataRef.current.message,
                                            isDismissed: true,
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            )

            return null
        }, [enqueueSnackbar]
    );

    const fetchHealthStatusMessage = useCallback(
        async () => {
            try {
                console.log('Fetching health status message from API...');
                const response = await fetch(`${HEALTH_STAT_API}/api/messages/active`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data: HealthStatusResponse = await response.json();

                if (data.success) {
                    if (data.message) {
                        const currentStoredData = storedDataRef.current;
                        if (
                            !currentStoredData?.message ||
                            currentStoredData.message.id !== data.message.id
                        ) {
                            const newMessage = { ...data.message, isDismissed: false };
                            setStoredData(
                                {
                                    ...data,
                                    message: newMessage,
                                }
                            )
                            await displayStatus(newMessage);

                        } else {
                            if (!currentStoredData.message.isDismissed) {
                                await displayStatus(currentStoredData.message);
                            }
                            setStoredData({
                                ...data,
                                message: {
                                    ...data.message,
                                    isDismissed: currentStoredData.message.isDismissed || false,
                                },
                            });
                        }
                    } else {
                        closeSnackbar('health-status-message');
                    }
                }

            } catch (error) {
                console.error('Error fetching health status message:', error);
            }
            return null
        }, [HEALTH_STAT_API, closeSnackbar, displayStatus]
    );


    useEffect(() => {
        if (env.features.disableHealthPoller) return;

        fetchHealthStatusMessage();

        intervalRef.current = setInterval(() => {
            fetchHealthStatusMessage();
        }, POLLING_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchHealthStatusMessage]);
};
